import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

const FA_ICONS = ['fa-solid fa-map-pin', 'fa-solid fa-globe-asia', 'fa-solid fa-landmark', 'fa-solid fa-building-columns', 'fa-solid fa-sun', 'fa-solid fa-star-and-crescent', 'fa-solid fa-globe-americas', 'fa-solid fa-water'];
const COLORS = ['#f97316', '#8b5cf6', '#0ea5e9', '#10b981', '#f59e0b', '#ec4899', '#3b82f6', '#14b8a6'];

const NavbarTab = ({ showToast }) => {
    const [regions, setRegions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openRegion, setOpenRegion] = useState(null);
    const [newDest, setNewDest] = useState({});
    const [newDestBold, setNewDestBold] = useState({});
    const [saving, setSaving] = useState(null);
    const [addRegionModal, setAddRegionModal] = useState(false);
    const [newRegion, setNewRegion] = useState({ region: '', fa_icon: FA_ICONS[0], color: COLORS[0], destinations: [], active: true, display_order: 0 });

    useEffect(() => { fetchRegions(); }, []);

    const fetchRegions = async () => {
        setLoading(true);
        const { data } = await supabase.from('navbar_config').select('*').order('display_order');
        setRegions(data || []);
        setLoading(false);
    };

    const updateRegion = async (id, payload) => {
        setSaving(id);
        const { error } = await supabase.from('navbar_config').update(payload).eq('id', id);
        if (!error) { showToast('Saved!', 'success'); fetchRegions(); }
        else showToast('Error: ' + error.message, 'error');
        setSaving(null);
    };

    const toggleActive = (reg) => updateRegion(reg.id, { active: !reg.active });

    const addDest = (reg) => {
        const val = (newDest[reg.id] || '').trim();
        if (!val) return;
        const isBold = !!newDestBold[reg.id];
        const dest = isBold ? { label: val, bold: true } : val;
        const destinations = [...(reg.destinations || []), dest];
        updateRegion(reg.id, { destinations });
        setNewDest(p => ({ ...p, [reg.id]: '' }));
        setNewDestBold(p => ({ ...p, [reg.id]: false }));
    };

    const removeDest = (reg, idx) => {
        const destinations = reg.destinations.filter((_, i) => i !== idx);
        updateRegion(reg.id, { destinations });
    };

    const toggleDestBold = (reg, idx) => {
        const destinations = reg.destinations.map((d, i) => {
            if (i !== idx) return d;
            if (typeof d === 'string') return { label: d, bold: true };
            return d.bold ? d.label : { label: d.label, bold: true };
        });
        updateRegion(reg.id, { destinations });
    };

    const getLabel = (d) => typeof d === 'string' ? d : d.label;
    const isBold = (d) => typeof d === 'object' && d.bold;

    const deleteRegion = async (id) => {
        await supabase.from('navbar_config').delete().eq('id', id);
        showToast('Region removed', 'success');
        fetchRegions();
    };

    const saveNewRegion = async () => {
        if (!newRegion.region) return showToast('Region name required', 'error');
        const { error } = await supabase.from('navbar_config').insert([{ ...newRegion, display_order: regions.length }]);
        if (!error) { showToast('Region added!', 'success'); fetchRegions(); setAddRegionModal(false); setNewRegion({ region: '', fa_icon: FA_ICONS[0], color: COLORS[0], destinations: [], active: true, display_order: 0 }); }
        else showToast('Error: ' + error.message, 'error');
    };

    return (
        <div className="admin-tab-content">
            <div className="admin-stats-row">
                <div className="admin-stat-card">
                    <div className="admin-stat-icon gold"><i className="fa-solid fa-bars" /></div>
                    <div><div className="admin-stat-val">{regions.length}</div><div className="admin-stat-lbl">Menu Regions</div></div>
                </div>
                <div className="admin-stat-card">
                    <div className="admin-stat-icon blue"><i className="fa-solid fa-location-dot" /></div>
                    <div><div className="admin-stat-val">{regions.reduce((a, r) => a + (r.destinations || []).length, 0)}</div><div className="admin-stat-lbl">Total Destinations</div></div>
                </div>
                <div className="admin-stat-card">
                    <div className="admin-stat-icon green"><i className="fa-solid fa-eye" /></div>
                    <div><div className="admin-stat-val">{regions.filter(r => r.active).length}</div><div className="admin-stat-lbl">Active Regions</div></div>
                </div>
            </div>

            <div className="admin-section-header">
                <div className="admin-section-title"><i className="fa-solid fa-bars" /> Holidays Mega Menu</div>
                <button className="admin-btn admin-btn-primary" onClick={() => setAddRegionModal(true)}>
                    <i className="fa-solid fa-plus" /> Add Region
                </button>
            </div>

            {loading ? <div className="admin-loading"><div className="admin-spinner" /><p>Loading menu config...</p></div>
                : regions.length === 0 ? <div className="admin-empty"><i className="fa-solid fa-bars" /><p>No menu regions yet.</p></div>
                    : regions.map(reg => (
                        <div className="admin-region-card" key={reg.id} style={{ opacity: reg.active ? 1 : 0.6 }}>
                            <div className="admin-region-header" onClick={() => setOpenRegion(openRegion === reg.id ? null : reg.id)}>
                                <div className="admin-region-icon" style={{ background: reg.color + '22', color: reg.color }}>
                                    <i className={reg.fa_icon} />
                                </div>
                                <div className="admin-region-name">{reg.region}</div>
                                <div className="admin-region-count">{(reg.destinations || []).length} destinations</div>
                                <span className={`badge ${reg.active ? 'badge-active' : 'badge-inactive'}`} style={{ marginRight: 8 }}>
                                    {reg.active ? 'Active' : 'Hidden'}
                                </span>
                                <button
                                    className={`admin-btn admin-btn-sm ${reg.active ? 'admin-btn-secondary' : 'admin-btn-primary'}`}
                                    onClick={e => { e.stopPropagation(); toggleActive(reg); }}
                                    style={{ marginRight: 8 }}
                                >
                                    <i className={`fa-solid ${reg.active ? 'fa-eye-slash' : 'fa-eye'}`} />
                                    {reg.active ? 'Hide' : 'Show'}
                                </button>
                                <button className="admin-btn-icon danger" onClick={e => { e.stopPropagation(); deleteRegion(reg.id); }}>
                                    <i className="fa-solid fa-trash" />
                                </button>
                                <i className={`fa-solid fa-chevron-down admin-region-chevron ${openRegion === reg.id ? 'open' : ''}`} />
                            </div>

                            {openRegion === reg.id && (
                                <div className="admin-region-body">
                                    <p style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 12 }}>
                                        Click a destination to toggle <span style={{ color: 'var(--gold)', fontWeight: 600 }}>bold</span> (combo package). Click × to remove.
                                    </p>
                                    <div className="admin-dest-list">
                                        {(reg.destinations || []).map((dest, idx) => (
                                            <div key={idx} className={`admin-dest-chip ${isBold(dest) ? 'bold-chip' : ''}`}>
                                                <span
                                                    onClick={() => toggleDestBold(reg, idx)}
                                                    style={{ cursor: 'pointer' }}
                                                    title="Click to toggle bold"
                                                >
                                                    {isBold(dest) && <i className="fa-solid fa-star" style={{ fontSize: 9, marginRight: 3 }} />}
                                                    {getLabel(dest)}
                                                </span>
                                                <button onClick={() => removeDest(reg, idx)} title="Remove">
                                                    <i className="fa-solid fa-xmark" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="admin-dest-add-row">
                                        <input
                                            className="admin-input"
                                            placeholder="Add destination name..."
                                            value={newDest[reg.id] || ''}
                                            onChange={e => setNewDest(p => ({ ...p, [reg.id]: e.target.value }))}
                                            onKeyDown={e => e.key === 'Enter' && addDest(reg)}
                                            style={{ flex: 1 }}
                                        />
                                        <div className="admin-toggle-row" style={{ padding: '8px 12px', background: 'var(--bg-card2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
                                            <span style={{ fontSize: 12, color: 'var(--text-2)', marginRight: 8 }}>Bold (combo)</span>
                                            <input className="admin-toggle" type="checkbox"
                                                checked={!!newDestBold[reg.id]}
                                                onChange={e => setNewDestBold(p => ({ ...p, [reg.id]: e.target.checked }))}
                                            />
                                        </div>
                                        <button className="admin-btn admin-btn-primary" onClick={() => addDest(reg)}>
                                            {saving === reg.id ? <i className="fa-solid fa-circle-notch fa-spin" /> : <><i className="fa-solid fa-plus" /> Add</>}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}

            {/* Add Region Modal */}
            {addRegionModal && (
                <div className="admin-modal-overlay" onClick={() => setAddRegionModal(false)}>
                    <div className="admin-modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h3><i className="fa-solid fa-plus" /> Add New Region</h3>
                            <button className="admin-modal-close" onClick={() => setAddRegionModal(false)}><i className="fa-solid fa-xmark" /></button>
                        </div>
                        <div className="admin-modal-body">
                            <div className="admin-form-grid" style={{ marginBottom: 16 }}>
                                <div className="admin-field admin-form-full">
                                    <label className="admin-label">Region Name *</label>
                                    <input className="admin-input" value={newRegion.region} onChange={e => setNewRegion(r => ({ ...r, region: e.target.value }))} placeholder="e.g. Southeast Asia" />
                                </div>
                                <div className="admin-field">
                                    <label className="admin-label">Icon (FontAwesome class)</label>
                                    <select className="admin-select" value={newRegion.fa_icon} onChange={e => setNewRegion(r => ({ ...r, fa_icon: e.target.value }))}>
                                        {FA_ICONS.map(i => <option key={i} value={i}>{i}</option>)}
                                    </select>
                                </div>
                                <div className="admin-field">
                                    <label className="admin-label">Accent Color</label>
                                    <div className="admin-color-row">
                                        {COLORS.map(c => (
                                            <div key={c} className={`admin-color-swatch ${newRegion.color === c ? 'selected' : ''}`}
                                                style={{ background: c }} onClick={() => setNewRegion(r => ({ ...r, color: c }))} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="admin-btn admin-btn-secondary" onClick={() => setAddRegionModal(false)}>Cancel</button>
                            <button className="admin-btn admin-btn-primary" onClick={saveNewRegion}><i className="fa-solid fa-plus" /> Add Region</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NavbarTab;
